/**
 * Coups de pouce — Attendu 637 « Modèles pré-algébriques ».
 *
 * Une entrée par `generator_key` (cf. gen_637.js). Contrat d'une entrée :
 *   pages : 2 pages MAXIMUM, chacune { titre, lignes?, svg? }.
 *           Page 1 « La règle »   : les phrases du mini-cours.
 *           Page 2 « Un exemple » : la figure qui les illustre.
 *
 * Règle absolue : l'aide porte sur d'AUTRES valeurs que celles que le
 * générateur peut tirer, sinon elle donne la réponse.
 * Emphase : **texte** (rendu en gras, pas de HTML brut). Pas de tiret cadratin.
 */

// ── Figure balance : 2 boîtes « ? » + 5 g d'un côté, 65 g de l'autre ──
// Boîte de 30 g : gen_637 tire des objets de 3 à 25, aucune collision possible.
const SVG_BALANCE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 200" role="img"
     aria-label="Balance en équilibre : deux boîtes inconnues et une masse de 5 g à gauche, une masse de 65 g à droite">
  <!-- Socle et pivot -->
  <rect x="160" y="158" width="40" height="30" rx="3" fill="#94a3b8" opacity="0.6"/>
  <rect x="140" y="150" width="80" height="12" rx="3" fill="#94a3b8" opacity="0.6"/>
  <polygon points="180,150 174,124 186,124" fill="#94a3b8" opacity="0.7"/>
  <circle cx="180" cy="122" r="4" fill="#94a3b8" stroke="#64748b" stroke-width="0.5"/>
  <!-- Fléau -->
  <line x1="80" y1="116" x2="280" y2="116" stroke="#0f172a" stroke-width="2.5" stroke-linecap="round"/>
  <!-- Chaînes -->
  <line x1="80" y1="116" x2="80" y2="92" stroke="#0f172a" stroke-width="1.2"/>
  <line x1="280" y1="116" x2="280" y2="92" stroke="#0f172a" stroke-width="1.2"/>
  <!-- Coupelles -->
  <rect x="20" y="92" width="120" height="8" rx="3" fill="#94a3b8" stroke="#64748b" stroke-width="0.5"/>
  <rect x="220" y="92" width="120" height="8" rx="3" fill="#94a3b8" stroke="#64748b" stroke-width="0.5"/>
  <!-- Plateau gauche : 2 boîtes ? + masse 5 g -->
  <rect x="26" y="60" width="28" height="30" rx="4" fill="#f59e0b" stroke="#d97706" stroke-width="0.8"/>
  <text x="40" y="81" font-size="16" font-weight="600" fill="#78350f" text-anchor="middle" font-family="system-ui,sans-serif">?</text>
  <rect x="60" y="60" width="28" height="30" rx="4" fill="#f59e0b" stroke="#d97706" stroke-width="0.8"/>
  <text x="74" y="81" font-size="16" font-weight="600" fill="#78350f" text-anchor="middle" font-family="system-ui,sans-serif">?</text>
  <rect x="94" y="66" width="40" height="24" rx="4" fill="#94a3b8" opacity="0.5" stroke="#64748b" stroke-width="0.8"/>
  <text x="114" y="83" font-size="14" font-weight="600" fill="#0f172a" text-anchor="middle" font-family="system-ui,sans-serif">5 g</text>
  <!-- Plateau droit : masse 65 g -->
  <rect x="245" y="52" width="70" height="38" rx="6" fill="#dbeafe" stroke="#93c5fd" stroke-width="0.8"/>
  <text x="280" y="77" font-size="17" font-weight="600" fill="#1e40af" text-anchor="middle" font-family="system-ui,sans-serif">65 g</text>
  <!-- Étiquette -->
  <text x="80" y="40" font-size="14" fill="#0f172a" text-anchor="middle" font-family="system-ui,sans-serif">2 boîtes identiques</text>
</svg>`.trim();

// Entrée commune aux formats de l'exercice balance_inconnue : même énoncé,
// même figure, même raisonnement — une retouche profite à tous les formats.
const CP_BALANCE = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                '**Équilibre** : les deux plateaux d\'une balance en équilibre portent exactement la même masse.',
                'On peut **retirer la même masse des deux côtés** : la balance reste en équilibre.',
                'S\'il reste plusieurs objets **identiques**, ils se partagent la masse restante **en parts égales**.',
            ],
        },
        {
            titre: 'Un exemple',
            svg: SVG_BALANCE,
            lignes: [
                'On retire **5 g** de chaque côté : il reste 65 − 5 = **60 g** pour les 2 boîtes.',
                'Les 2 boîtes sont identiques : chacune pèse 60 ÷ 2 = **30 g**.',
            ],
        },
    ],
};

// ── Figure répartition : les niveaux d'eau, résolution ANIMÉE ──
// Total 100, écarts 8 et 4 : gen_637 tire des totaux dans [120..500] et des
// écarts multiples de 10, aucune collision possible.
// Trois verres identiques, niveaux 40 | 32 | 28 (échelle 2 px par unité), la
// ligne pointillée marque le niveau cible. Animation SMIL (sans JS, la sheet
// injecte le SVG inline), démarrée PAR L'ÉLÈVE via le bouton « Lancer »
// (begin="cp637play.click"), jouée UNE FOIS (7 s) puis figée sur l'état final
// (fill="freeze") : ① le trop-plein orange du 1er se vide, ② le niveau du 3e
// monte jusqu'à la ligne, ③ les « 32 », la division et les trois phrases de
// réponse apparaissent et RESTENT affichées. Avant le clic, le SVG montre
// l'état de départ (attributs de base = état initial). Re-cliquer rejoue.
const SVG_VERRES_ANIM = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 246" role="img"
     aria-label="Schéma : trois verres à 40, 32 et 28 avec une ligne de niveau cible. Le bouton Lancer démarre l'animation : le premier verre verse son trop-plein de 8, le troisième se remplit de 4, les trois verres arrivent au même niveau de 32, et 96 divisé par 3 égale 32. Le premier a 40, le deuxième 32, le troisième 28">
  <text x="8" y="20" font-size="13" font-weight="600" fill="#64748b" font-family="system-ui,sans-serif">Au départ, total : 100<animate attributeName="opacity" values="1;1;0;0" keyTimes="0;0.12;0.26;1" dur="7s" begin="cp637play.click" fill="freeze"/></text>
  <text x="8" y="20" font-size="13" font-weight="600" fill="#64748b" opacity="0" font-family="system-ui,sans-serif">Le total devient 100 − 8 + 4 = 96<animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.12;0.3;1" dur="7s" begin="cp637play.click" fill="freeze"/></text>
  <!-- bouton Lancer (SMIL : begin="cp637play.click" sur toutes les animations) -->
  <g id="cp637play" style="cursor:pointer">
    <title>Lancer l'animation</title>
    <rect x="266" y="4" width="86" height="24" rx="12" fill="#1E3A8A"/>
    <path d="M278 10 L278 22 L288 16 Z" fill="#ffffff"/>
    <text x="322" y="20" font-size="12" font-weight="600" fill="#ffffff" text-anchor="middle" font-family="system-ui,sans-serif">Lancer</text>
  </g>
  <line x1="30" y1="70" x2="330" y2="70" stroke="#94a3b8" stroke-width="1.2" stroke-dasharray="5 4"/>
  <!-- verre 1 : l'eau reste à 32, le trop-plein orange se vide -->
  <rect x="40" y="70" width="70" height="64" fill="#1E3A8A"/>
  <rect x="40" y="54" width="70" height="16" fill="#F97316">
    <animate attributeName="y" values="54;54;70;70" keyTimes="0;0.12;0.3;1" dur="7s" begin="cp637play.click" fill="freeze"/>
    <animate attributeName="height" values="16;16;0;0" keyTimes="0;0.12;0.3;1" dur="7s" begin="cp637play.click" fill="freeze"/>
  </rect>
  <text x="75" y="67" font-size="12" font-weight="700" fill="#ffffff" text-anchor="middle" font-family="system-ui,sans-serif">8<animate attributeName="opacity" values="1;1;0;0" keyTimes="0;0.12;0.26;1" dur="7s" begin="cp637play.click" fill="freeze"/></text>
  <path d="M38 38 L38 136 L112 136 L112 38" fill="none" stroke="#64748b" stroke-width="2"/>
  <!-- verre 2 : déjà au niveau -->
  <rect x="145" y="70" width="70" height="64" fill="#1E3A8A"/>
  <path d="M143 38 L143 136 L217 136 L217 38" fill="none" stroke="#64748b" stroke-width="2"/>
  <!-- verre 3 : l'eau monte de 28 à 32 -->
  <rect x="250" y="78" width="70" height="56" fill="#1E3A8A">
    <animate attributeName="y" values="78;78;70;70" keyTimes="0;0.38;0.55;1" dur="7s" begin="cp637play.click" fill="freeze"/>
    <animate attributeName="height" values="56;56;64;64" keyTimes="0;0.38;0.55;1" dur="7s" begin="cp637play.click" fill="freeze"/>
  </rect>
  <rect x="250" y="70" width="70" height="8" fill="none" stroke="#dc2626" stroke-width="1.5" stroke-dasharray="4 3">
    <animate attributeName="opacity" values="1;1;0;0" keyTimes="0;0.38;0.52;1" dur="7s" begin="cp637play.click" fill="freeze"/>
  </rect>
  <text x="332" y="78" font-size="12" font-weight="700" fill="#dc2626" font-family="system-ui,sans-serif">4<animate attributeName="opacity" values="1;1;0;0" keyTimes="0;0.38;0.52;1" dur="7s" begin="cp637play.click" fill="freeze"/></text>
  <path d="M248 38 L248 136 L322 136 L322 38" fill="none" stroke="#64748b" stroke-width="2"/>
  <!-- niveaux 32 révélés -->
  <text x="75" y="106" font-size="14" font-weight="700" fill="#ffffff" opacity="0" text-anchor="middle" font-family="system-ui,sans-serif">32<animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.6;0.72;1" dur="7s" begin="cp637play.click" fill="freeze"/></text>
  <text x="180" y="106" font-size="14" font-weight="700" fill="#ffffff" opacity="0" text-anchor="middle" font-family="system-ui,sans-serif">32<animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.6;0.72;1" dur="7s" begin="cp637play.click" fill="freeze"/></text>
  <text x="285" y="106" font-size="14" font-weight="700" fill="#ffffff" opacity="0" text-anchor="middle" font-family="system-ui,sans-serif">32<animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.6;0.72;1" dur="7s" begin="cp637play.click" fill="freeze"/></text>
  <!-- étiquettes -->
  <text x="75" y="154" font-size="13" fill="#0f172a" text-anchor="middle" font-family="system-ui,sans-serif">1er</text>
  <text x="180" y="154" font-size="13" fill="#0f172a" text-anchor="middle" font-family="system-ui,sans-serif">2e</text>
  <text x="285" y="154" font-size="13" fill="#0f172a" text-anchor="middle" font-family="system-ui,sans-serif">3e</text>
  <!-- la division, en conclusion -->
  <text x="180" y="176" font-size="14" font-weight="600" fill="#0f172a" opacity="0" text-anchor="middle" font-family="system-ui,sans-serif">96 ÷ 3 = 32<animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.66;0.78;1" dur="7s" begin="cp637play.click" fill="freeze"/></text>
  <!-- les trois réponses -->
  <g opacity="0">
    <rect x="30" y="192" width="8" height="8" rx="2" fill="#1E3A8A"/>
    <text x="46" y="200" font-size="13" fill="#0f172a" font-family="system-ui,sans-serif">Le 1er reçoit 40 billes (32 + 8).</text>
    <animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.76;0.84;1" dur="7s" begin="cp637play.click" fill="freeze"/>
  </g>
  <g opacity="0">
    <rect x="30" y="212" width="8" height="8" rx="2" fill="#1E3A8A"/>
    <text x="46" y="220" font-size="13" fill="#0f172a" font-family="system-ui,sans-serif">Le 2e reçoit 32 billes.</text>
    <animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.8;0.88;1" dur="7s" begin="cp637play.click" fill="freeze"/>
  </g>
  <g opacity="0">
    <rect x="30" y="232" width="8" height="8" rx="2" fill="#1E3A8A"/>
    <text x="46" y="240" font-size="13" fill="#0f172a" font-family="system-ui,sans-serif">Le 3e reçoit 28 billes (32 − 4).</text>
    <animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.84;0.92;1" dur="7s" begin="cp637play.click" fill="freeze"/>
  </g>
</svg>`.trim();

// ── Variante à 2 parts (format saisie) : total 46, écart 6 → 26 | 20 ──
// Mêmes principes que la version 3 verres ; 46 est hors de la liste fermée des
// totaux du générateur. Échelle 2,5 px par unité. Bouton id distinct (cp637play2).
const SVG_VERRES_2_ANIM = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 228" role="img"
     aria-label="Schéma : deux verres à 26 et 20 avec une ligne de niveau cible. Le bouton Lancer démarre l'animation : le premier verre verse son trop-plein de 6, les deux verres arrivent au même niveau de 20, et 40 divisé par 2 égale 20. Le premier reçoit 26 billes, le deuxième 20">
  <text x="8" y="20" font-size="13" font-weight="600" fill="#64748b" font-family="system-ui,sans-serif">Au départ, total : 46<animate attributeName="opacity" values="1;1;0;0" keyTimes="0;0.15;0.3;1" dur="7s" begin="cp637play2.click" fill="freeze"/></text>
  <text x="8" y="20" font-size="13" font-weight="600" fill="#64748b" opacity="0" font-family="system-ui,sans-serif">Le total devient 46 − 6 = 40<animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.15;0.34;1" dur="7s" begin="cp637play2.click" fill="freeze"/></text>
  <g id="cp637play2" style="cursor:pointer">
    <title>Lancer l'animation</title>
    <rect x="266" y="4" width="86" height="24" rx="12" fill="#1E3A8A"/>
    <path d="M278 10 L278 22 L288 16 Z" fill="#ffffff"/>
    <text x="322" y="20" font-size="12" font-weight="600" fill="#ffffff" text-anchor="middle" font-family="system-ui,sans-serif">Lancer</text>
  </g>
  <line x1="60" y1="86" x2="300" y2="86" stroke="#94a3b8" stroke-width="1.2" stroke-dasharray="5 4"/>
  <!-- verre 1 : l'eau reste à 20, le trop-plein de 6 (orange) se vide -->
  <rect x="90" y="86" width="70" height="50" fill="#1E3A8A"/>
  <rect x="90" y="71" width="70" height="15" fill="#F97316">
    <animate attributeName="y" values="71;71;86;86" keyTimes="0;0.15;0.35;1" dur="7s" begin="cp637play2.click" fill="freeze"/>
    <animate attributeName="height" values="15;15;0;0" keyTimes="0;0.15;0.35;1" dur="7s" begin="cp637play2.click" fill="freeze"/>
  </rect>
  <text x="125" y="83" font-size="12" font-weight="700" fill="#ffffff" text-anchor="middle" font-family="system-ui,sans-serif">6<animate attributeName="opacity" values="1;1;0;0" keyTimes="0;0.15;0.3;1" dur="7s" begin="cp637play2.click" fill="freeze"/></text>
  <path d="M88 48 L88 136 L162 136 L162 48" fill="none" stroke="#64748b" stroke-width="2"/>
  <!-- verre 2 : déjà au niveau -->
  <rect x="200" y="86" width="70" height="50" fill="#1E3A8A"/>
  <path d="M198 48 L198 136 L272 136 L272 48" fill="none" stroke="#64748b" stroke-width="2"/>
  <!-- niveaux 20 révélés -->
  <text x="125" y="116" font-size="14" font-weight="700" fill="#ffffff" opacity="0" text-anchor="middle" font-family="system-ui,sans-serif">20<animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.45;0.58;1" dur="7s" begin="cp637play2.click" fill="freeze"/></text>
  <text x="235" y="116" font-size="14" font-weight="700" fill="#ffffff" opacity="0" text-anchor="middle" font-family="system-ui,sans-serif">20<animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.45;0.58;1" dur="7s" begin="cp637play2.click" fill="freeze"/></text>
  <!-- étiquettes -->
  <text x="125" y="154" font-size="13" fill="#0f172a" text-anchor="middle" font-family="system-ui,sans-serif">1er</text>
  <text x="235" y="154" font-size="13" fill="#0f172a" text-anchor="middle" font-family="system-ui,sans-serif">2e</text>
  <!-- la division, en conclusion -->
  <text x="180" y="176" font-size="14" font-weight="600" fill="#0f172a" opacity="0" text-anchor="middle" font-family="system-ui,sans-serif">40 ÷ 2 = 20<animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.55;0.7;1" dur="7s" begin="cp637play2.click" fill="freeze"/></text>
  <!-- les deux réponses -->
  <g opacity="0">
    <rect x="30" y="192" width="8" height="8" rx="2" fill="#1E3A8A"/>
    <text x="46" y="200" font-size="13" fill="#0f172a" font-family="system-ui,sans-serif">Le 1er reçoit 26 billes (20 + 6).</text>
    <animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.72;0.8;1" dur="7s" begin="cp637play2.click" fill="freeze"/>
  </g>
  <g opacity="0">
    <rect x="30" y="212" width="8" height="8" rx="2" fill="#1E3A8A"/>
    <text x="46" y="220" font-size="13" fill="#0f172a" font-family="system-ui,sans-serif">Le 2e reçoit 20 billes.</text>
    <animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.78;0.86;1" dur="7s" begin="cp637play2.click" fill="freeze"/>
  </g>
</svg>`.trim();

// Entrée commune aux formats de l'exercice repartition_ecarts.
// Une seule page : un énoncé d'exemple, et l'animation qui le résout en boucle.
const CP_REPART = {
    pages: [
        {
            titre: 'Un exemple',
            lignes: [
                'On partage **100** billes entre trois amis. Le 1er en reçoit **8 de plus** que le 2e, le 3e en reçoit **4 de moins** que le 2e.',
            ],
            svg: SVG_VERRES_ANIM,
        },
    ],
};

// Variante 2 parts (le format saisie partage entre DEUX enfants).
const CP_REPART_2 = {
    pages: [
        {
            titre: 'Un exemple',
            lignes: [
                'On partage **46** billes entre deux amis. Le 1er en reçoit **6 de plus** que le 2e.',
            ],
            svg: SVG_VERRES_2_ANIM,
        },
    ],
};

// ── Substitution : comparer deux achats, faire disparaître le commun ──
// Trousse 6 € / carnet 5 € : objets ABSENTS des contextes du générateur,
// aucune collision possible. Animation SMIL au clic (id cp637play3), une
// lecture puis gel : l'achat de Tom s'encadre puis s'estompe DANS celui de
// Léa → il reste 2 trousses pour 23 − 11 = 12 € → 12 ÷ 2 = 6.
const SVG_SUBSTIT_ANIM = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 222" role="img"
     aria-label="Animation : Léa achète 3 trousses et 1 carnet pour 23 euros, Tom achète 1 trousse et 1 carnet pour 11 euros. On retire l'achat de Tom de celui de Léa : il reste 2 trousses pour 12 euros, donc une trousse coûte 6 euros et un carnet 5 euros">
  <text x="8" y="20" font-size="13" font-weight="600" fill="#64748b" font-family="system-ui,sans-serif">Au départ : deux achats<animate attributeName="opacity" values="1;1;0;0" keyTimes="0;0.3;0.42;1" dur="7s" begin="cp637play3.click" fill="freeze"/></text>
  <text x="8" y="20" font-size="13" font-weight="600" fill="#64748b" opacity="0" font-family="system-ui,sans-serif">On retire l'achat de Tom, il reste 23 − 11 = 12 €<animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.34;0.48;1" dur="7s" begin="cp637play3.click" fill="freeze"/></text>
  <g id="cp637play3" style="cursor:pointer">
    <title>Lancer l'animation</title>
    <rect x="266" y="4" width="86" height="24" rx="12" fill="#1E3A8A"/>
    <path d="M278 10 L278 22 L288 16 Z" fill="#ffffff"/>
    <text x="322" y="20" font-size="12" font-weight="600" fill="#ffffff" text-anchor="middle" font-family="system-ui,sans-serif">Lancer</text>
  </g>
  <!-- légende -->
  <rect x="30" y="30" width="11" height="11" rx="2" fill="#1E3A8A"/>
  <text x="46" y="40" font-size="12" fill="#0f172a" font-family="system-ui,sans-serif">trousse</text>
  <circle cx="112" cy="35" r="6" fill="#F97316"/>
  <text x="122" y="40" font-size="12" fill="#0f172a" font-family="system-ui,sans-serif">carnet</text>
  <!-- achat de Léa -->
  <text x="8" y="72" font-size="13" fill="#0f172a" font-family="system-ui,sans-serif">Léa</text>
  <rect x="50" y="54" width="26" height="26" rx="4" fill="#1E3A8A"/>
  <rect x="82" y="54" width="26" height="26" rx="4" fill="#1E3A8A"/>
  <g>
    <rect x="114" y="54" width="26" height="26" rx="4" fill="#1E3A8A"/>
    <circle cx="166" cy="67" r="13" fill="#F97316"/>
    <animate attributeName="opacity" values="1;1;0.15;0.15" keyTimes="0;0.28;0.42;1" dur="7s" begin="cp637play3.click" fill="freeze"/>
  </g>
  <rect x="110" y="50" width="73" height="34" rx="8" fill="none" stroke="#dc2626" stroke-width="1.5" stroke-dasharray="5 3" opacity="0">
    <animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.12;0.2;1" dur="7s" begin="cp637play3.click" fill="freeze"/>
  </rect>
  <text x="205" y="72" font-size="14" font-weight="600" fill="#0f172a" font-family="system-ui,sans-serif">23 €</text>
  <!-- accolade sous les 2 trousses restantes -->
  <path d="M50 88 L50 93 L108 93 L108 88" fill="none" stroke="#64748b" stroke-width="1.2" opacity="0">
    <animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.45;0.56;1" dur="7s" begin="cp637play3.click" fill="freeze"/>
  </path>
  <text x="79" y="106" font-size="12" font-weight="600" fill="#0f172a" text-anchor="middle" opacity="0" font-family="system-ui,sans-serif">12 €<animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.45;0.56;1" dur="7s" begin="cp637play3.click" fill="freeze"/></text>
  <!-- achat de Tom -->
  <text x="8" y="138" font-size="13" fill="#0f172a" font-family="system-ui,sans-serif">Tom</text>
  <rect x="50" y="120" width="26" height="26" rx="4" fill="#1E3A8A"/>
  <circle cx="102" cy="133" r="13" fill="#F97316"/>
  <rect x="46" y="116" width="73" height="34" rx="8" fill="none" stroke="#dc2626" stroke-width="1.5" stroke-dasharray="5 3" opacity="0">
    <animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.12;0.2;1" dur="7s" begin="cp637play3.click" fill="freeze"/>
  </rect>
  <text x="205" y="138" font-size="14" font-weight="600" fill="#0f172a" font-family="system-ui,sans-serif">11 €</text>
  <!-- la division -->
  <text x="180" y="170" font-size="14" font-weight="600" fill="#0f172a" opacity="0" text-anchor="middle" font-family="system-ui,sans-serif">12 ÷ 2 = 6 € la trousse<animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.58;0.72;1" dur="7s" begin="cp637play3.click" fill="freeze"/></text>
  <!-- conclusions -->
  <g opacity="0">
    <rect x="30" y="184" width="8" height="8" rx="2" fill="#1E3A8A"/>
    <text x="46" y="192" font-size="13" fill="#0f172a" font-family="system-ui,sans-serif">Une trousse coûte 6 € (12 ÷ 2).</text>
    <animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.76;0.84;1" dur="7s" begin="cp637play3.click" fill="freeze"/>
  </g>
  <g opacity="0">
    <rect x="30" y="206" width="8" height="8" rx="2" fill="#1E3A8A"/>
    <text x="46" y="214" font-size="13" fill="#0f172a" font-family="system-ui,sans-serif">Un carnet coûte 5 € (11 − 6).</text>
    <animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.82;0.9;1" dur="7s" begin="cp637play3.click" fill="freeze"/>
  </g>
</svg>`.trim();

// ── Page 2 : le cas NON emboîté (famille 2A+1B / 1A+2B, 20 % des tirages) ──
// Aucun achat n'est contenu dans l'autre : on REGROUPE tout → 3 trousses et
// 3 carnets = 33 € → 3 paires identiques → 33 ÷ 3 = 11 € la paire.
// Les 6 objets migrent en 3 paires par animateTransform (id cp637play4).
const SVG_SUBSTIT2_ANIM = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 280" role="img"
     aria-label="Animation : Léa achète 2 trousses et 1 carnet pour 17 euros, Tom achète 1 trousse et 2 carnets pour 16 euros. Aucun achat n'est contenu dans l'autre : on regroupe tout, 33 euros pour 3 paires identiques, donc une paire coûte 11 euros, une trousse 6 euros et un carnet 5 euros">
  <text x="8" y="20" font-size="13" font-weight="600" fill="#64748b" font-family="system-ui,sans-serif">Au départ : deux achats<animate attributeName="opacity" values="1;1;0;0" keyTimes="0;0.26;0.38;1" dur="7s" begin="cp637play4.click" fill="freeze"/></text>
  <text x="8" y="20" font-size="13" font-weight="600" fill="#64748b" opacity="0" font-family="system-ui,sans-serif">On regroupe tout, cela fait 17 + 16 = 33 €<animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.28;0.42;1" dur="7s" begin="cp637play4.click" fill="freeze"/></text>
  <g id="cp637play4" style="cursor:pointer">
    <title>Lancer l'animation</title>
    <rect x="266" y="4" width="86" height="24" rx="12" fill="#1E3A8A"/>
    <path d="M278 10 L278 22 L288 16 Z" fill="#ffffff"/>
    <text x="322" y="20" font-size="12" font-weight="600" fill="#ffffff" text-anchor="middle" font-family="system-ui,sans-serif">Lancer</text>
  </g>
  <!-- achat de Léa : 2 trousses + 1 carnet -->
  <text x="8" y="72" font-size="13" fill="#0f172a" font-family="system-ui,sans-serif">Léa</text>
  <rect x="50" y="54" width="26" height="26" rx="4" fill="#1E3A8A">
    <animateTransform attributeName="transform" type="translate" values="0 0;0 0;-10 116;-10 116" keyTimes="0;0.3;0.5;1" dur="7s" begin="cp637play4.click" fill="freeze"/>
  </rect>
  <rect x="82" y="54" width="26" height="26" rx="4" fill="#1E3A8A">
    <animateTransform attributeName="transform" type="translate" values="0 0;0 0;58 116;58 116" keyTimes="0;0.3;0.5;1" dur="7s" begin="cp637play4.click" fill="freeze"/>
  </rect>
  <circle cx="140" cy="67" r="13" fill="#F97316">
    <animateTransform attributeName="transform" type="translate" values="0 0;0 0;-62 116;-62 116" keyTimes="0;0.3;0.5;1" dur="7s" begin="cp637play4.click" fill="freeze"/>
  </circle>
  <text x="205" y="72" font-size="14" font-weight="600" fill="#0f172a" font-family="system-ui,sans-serif">17 €</text>
  <!-- achat de Tom : 1 trousse + 2 carnets -->
  <text x="8" y="128" font-size="13" fill="#0f172a" font-family="system-ui,sans-serif">Tom</text>
  <rect x="50" y="110" width="26" height="26" rx="4" fill="#1E3A8A">
    <animateTransform attributeName="transform" type="translate" values="0 0;0 0;190 60;190 60" keyTimes="0;0.3;0.5;1" dur="7s" begin="cp637play4.click" fill="freeze"/>
  </rect>
  <circle cx="102" cy="123" r="13" fill="#F97316">
    <animateTransform attributeName="transform" type="translate" values="0 0;0 0;76 60;76 60" keyTimes="0;0.3;0.5;1" dur="7s" begin="cp637play4.click" fill="freeze"/>
  </circle>
  <circle cx="140" cy="123" r="13" fill="#F97316">
    <animateTransform attributeName="transform" type="translate" values="0 0;0 0;138 60;138 60" keyTimes="0;0.3;0.5;1" dur="7s" begin="cp637play4.click" fill="freeze"/>
  </circle>
  <text x="205" y="128" font-size="14" font-weight="600" fill="#0f172a" font-family="system-ui,sans-serif">16 €</text>
  <!-- cadres des 3 paires identiques -->
  <rect x="34" y="162" width="62" height="40" rx="8" fill="none" stroke="#64748b" stroke-width="1.5" opacity="0">
    <animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.52;0.62;1" dur="7s" begin="cp637play4.click" fill="freeze"/>
  </rect>
  <rect x="134" y="162" width="62" height="40" rx="8" fill="none" stroke="#64748b" stroke-width="1.5" opacity="0">
    <animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.52;0.62;1" dur="7s" begin="cp637play4.click" fill="freeze"/>
  </rect>
  <rect x="234" y="162" width="62" height="40" rx="8" fill="none" stroke="#64748b" stroke-width="1.5" opacity="0">
    <animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.52;0.62;1" dur="7s" begin="cp637play4.click" fill="freeze"/>
  </rect>
  <!-- la division -->
  <text x="180" y="222" font-size="14" font-weight="600" fill="#0f172a" opacity="0" text-anchor="middle" font-family="system-ui,sans-serif">3 paires identiques, donc 33 ÷ 3 = 11 € la paire<animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.6;0.72;1" dur="7s" begin="cp637play4.click" fill="freeze"/></text>
  <!-- conclusions -->
  <g opacity="0">
    <rect x="30" y="234" width="8" height="8" rx="2" fill="#1E3A8A"/>
    <text x="46" y="242" font-size="13" fill="#0f172a" font-family="system-ui,sans-serif">Une trousse coûte 6 € (17 − 11).</text>
    <animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.76;0.84;1" dur="7s" begin="cp637play4.click" fill="freeze"/>
  </g>
  <g opacity="0">
    <rect x="30" y="256" width="8" height="8" rx="2" fill="#1E3A8A"/>
    <text x="46" y="264" font-size="13" fill="#0f172a" font-family="system-ui,sans-serif">Un carnet coûte 5 € (11 − 6).</text>
    <animate attributeName="opacity" values="0;0;1;1" keyTimes="0;0.82;0.9;1" dur="7s" begin="cp637play4.click" fill="freeze"/>
  </g>
</svg>`.trim();

// Entrée substitution : 2 pages — achat emboîté (page 1), regroupement (page 2).
const CP_SUBSTIT = {
    pages: [
        {
            titre: 'Un exemple',
            lignes: [
                'Léa achète **3 trousses et 1 carnet** pour **23 €**. Tom achète **1 trousse et 1 carnet** pour **11 €**.',
            ],
            svg: SVG_SUBSTIT_ANIM,
        },
        {
            titre: 'Un autre cas',
            lignes: [
                'Léa achète **2 trousses et 1 carnet** pour **17 €**. Tom achète **1 trousse et 2 carnets** pour **16 €**.',
            ],
            svg: SVG_SUBSTIT2_ANIM,
        },
    ],
};

const CP_637 = {
    balance_inconnue_qcm: CP_BALANCE,
    balance_inconnue_saisie: CP_BALANCE,
    balance_inconnue_texte_trou: CP_BALANCE,
    repartition_ecarts_qcm: CP_REPART,
    repartition_ecarts_saisie: CP_REPART_2,
    repartition_ecarts_texte_trou: CP_REPART,
    substitution_prix_qcm: CP_SUBSTIT,
    substitution_prix_saisie: CP_SUBSTIT,
    substitution_prix_relier: CP_SUBSTIT,
};

export default CP_637;
